/**
 * 把资产库的平铺条目归成「一次任务一组」—— **唯一实现**。
 *
 * 为什么单独一个文件：这份规则现在有两个消费者，
 *   · `pages/MyWorks.tsx`   —— 我的作品（一次生成 = 一个任务图集）
 *   · `pages/AiModels.tsx`  —— AI 模特库（一次生成 = **一个人**）
 * 两处各写一遍就是本项目头号 bug 类型（同一份规则两处各写，早晚分叉）。
 *
 * ⚠️ 它只是**归组**，不负责「组长什么样」：两个页面的卡片渲染
 * （`TaskCard` 的任务图集 vs 模特卡）差得很远，那部分各写各的。
 */

import type { AssetLibraryItem } from '@/api/types'

/**
 * 一组资产 = 一次任务（或一件孤立的素材）。
 *
 * 为什么必须归组：以前「我的作品」是**一件资产一张卡**的平铺网格。
 * 一次「白底主图 × 4 张」会被拆成 4 张互不相识的卡片，用户看到的是
 * 「怎么又出了 4 张一样的」，而且**点哪张都点不开**。
 * 任务才是用户心里的单位，图集只是它的产出。
 *
 * 同一个结论在 AI 模特库里更硬：`product-product-model` 的第 1 张是正面单人照、
 * 第 2 张是同一个人的三视图（提示词里明文写着「各张之间保持同一个人」）。
 * 拆开摆就是「两张看起来像但不是一个人」——完全错的语义。
 */
export type AssetGroup = {
  key: string
  /** 后端任务 id；空串 = 不属于任何任务（上传素材，或无法归组的存量作品） */
  jobId: string
  title: string
  items: AssetLibraryItem[]
  createdAt: string
}

/** 同一任务的每张产出标题可能带 `#2` 序号，去掉它才是任务名 */
export function groupTitle(items: AssetLibraryItem[]): string {
  const raw = items[0]?.title ?? '未命名'
  const cleaned = raw.replace(/\s*#\d+\s*$/, '').trim()
  return cleaned || raw
}

/**
 * 按 `jobId` 归组；没有任务号的单张成组。
 *
 * ⚠️ 判据是 `origin === 'generated' && jobId`：
 *   · 上传素材没有任务号，天生单张；
 *   · 存量作品（回填前写入、`archive_key` 不成形）也单张 ——
 *     宁可不成组，也不硬凑（硬凑的后果是两张不相干的图被并成「一个模特」）。
 *
 * 返回**按时间倒序**：新组的第 1 张产出最先落，用户先看到刚跑的那批。
 */
export function groupByTask(items: AssetLibraryItem[]): AssetGroup[] {
  const byJob = new Map<string, AssetLibraryItem[]>()
  const alone: AssetGroup[] = []

  for (const it of items) {
    const jid = (it.jobId ?? '').trim()
    if (it.origin === 'generated' && jid) {
      const arr = byJob.get(jid) ?? []
      arr.push(it)
      byJob.set(jid, arr)
    } else {
      alone.push({
        key: `one:${it.id}`,
        jobId: '',
        title: it.title,
        items: [it],
        createdAt: it.createdAt,
      })
    }
  }

  const albums: AssetGroup[] = [...byJob.entries()].map(([jid, arr]) => ({
    key: `job:${jid}`,
    jobId: jid,
    title: groupTitle(arr),
    // 组内按时间正序：第 1 张就该是第 1 张
    items: [...arr].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)),
    createdAt: arr[0].createdAt,
  }))

  return [...albums, ...alone].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
}
